<template>
  <div class="labels-page flex-1 min-h-0 flex flex-col p-4">
    <woot-button
      type="button"
      color-scheme="success"
      class-names="button--fixed-top"
      icon="add-circle"
      @click="openAddPopup"
    >
      {{ $t('LABEL_MGMT.HEADER_BTN_TXT') }}
    </woot-button>

    <div
      class="labels-page__body flex-1 min-h-0 flex flex-col"
      :class="{ 'labels-page__body--with-footer': showTableFooter }"
    >
      <div class="labels-header">
        <!-- Toggle Tabs -->
        <div class="tabs-container">
          <button
            type="button"
            class="tab-button"
            :class="{ active: activeTab === 'all' }"
            @click="onTabChange('all')"
          >
            All
          </button>
          <button
            type="button"
            class="tab-button"
            :class="{ active: activeTab === 'labels' }"
            @click="onTabChange('labels')"
          >
            Normal
          </button>
          <button
            type="button"
            class="tab-button"
            :class="{ active: activeTab === 'automation' }"
            @click="onTabChange('automation')"
          >
            Automation
          </button>
          <button
            type="button"
            class="tab-button"
            :class="{ active: activeTab === 'excluded' }"
            @click="onTabChange('excluded')"
          >
            Excluded
          </button>
        </div>

        <div class="label-search-wrap">
          <div class="search-icon-wrap" @click="applySearch">
            <fluent-icon icon="search" class="search-icon" />
          </div>
          <input
            type="text"
            :placeholder="$t('LABEL_MGMT.SEARCH_PLACEHOLDER')"
            class="label-search-input"
            :value="searchQuery"
            @keyup.enter="applySearch"
            @input="onSearchInput"
          />
        </div>
      </div>

      <div class="flex flex-row gap-4 flex-1 min-h-0 overflow-hidden">
        <div class="w-[60%] labels-list-column flex-1 min-h-0 overflow-auto">
          <woot-loading-state
            v-if="uiFlags.isFetching || !hasLoadedOnce"
          />
          <p
            v-else-if="!filteredRecords.length"
            class="labels-empty-state"
          >
            {{ emptyStateMessage }}
          </p>

          <table v-else class="woot-table labels-table">
          <thead>
            <th
              v-for="thHeader in $t('LABEL_MGMT.LIST.TABLE_HEADER')"
              :key="thHeader"
            >
              {{ thHeader }}
            </th>
          </thead>
          <tbody>
            <tr v-for="(label, index) in paginatedRecords" :key="label.id || label.title">
              <td class="label-title">
                <span
                  v-tooltip="label.title.length > 25 ? label.title : null"
                  class="overflow-hidden whitespace-nowrap text-ellipsis"
                >
                  {{ label.title }}
                </span>
              </td>
              <td class="label-description">
                <span
                  v-tooltip.right="descriptionTooltip(label.description)"
                  class="label-description-text"
                >
                  {{ label.description }}
                </span>
              </td>
              <td class="label-color-cell">
                <div class="label-color--container">
                  <!-- Automation robot icon for automation labels -->
                  <div v-if="label.automation || label.contact_automation" class="automation-icon-wrap">
                    <svg
                      class="automation-robot-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      :style="{ color: label.color }"
                    >
                      <path d="M13 2v2h-2V2h2zm-1 4a6 6 0 0 0-6 6v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6a6 6 0 0 0-6-6zM8 12h2v2H8v-2zm6 0h2v2h-2v-2zm-5 5h6v2H9v-2zM2 10h2v6H2v-6zm18 0h2v6h-2v-6z"/>
                    </svg>
                  </div>
                  <div
                    v-if="label.exclude_from_default_views"
                    v-tooltip.top="
                      $t('LABEL_MGMT.FORM.SHOW_ON_SIDEBAR.EXCLUDE_FROM_DEFAULT_VIEWS')
                    "
                    class="automation-icon-wrap"
                  >
                    <fluent-icon
                      icon="eye-hide"
                      size="16"
                      :style="{ color: label.color }"
                    />
                  </div>
                  <!-- Color square for normal labels -->
                  <span
                    v-else-if="!label.automation && !label.contact_automation"
                    class="label-color--display"
                    :style="{ backgroundColor: label.color }"
                  />
                  {{ label.color }}
                </div>
              </td>
              <td class="button-wrapper">
                <woot-button
                  type="button"
                  v-tooltip.top="$t('LABEL_MGMT.FORM.EDIT')"
                  variant="smooth"
                  size="tiny"
                  color-scheme="secondary"
                  class-names="grey-btn"
                  :is-loading="editLoading[label.id]"
                  icon="edit"
                  @click="openEditPopup(label)"
                />
                <woot-button
                  type="button"
                  v-tooltip.top="$t('LABEL_MGMT.FORM.DELETE')"
                  variant="smooth"
                  color-scheme="alert"
                  size="tiny"
                  icon="dismiss-circle"
                  class-names="grey-btn"
                  :is-loading="deleteLoading[label.id]"
                  @click="openDeletePopup(label, index)"
                />
              </td>
            </tr>
          </tbody>
        </table>
        </div>

        <div class="w-[34%] labels-sidebar overflow-auto">
          <span v-dompurify-html="$t('LABEL_MGMT.SIDEBAR_TXT')" />
        </div>
      </div>
    </div>

    <div
      v-if="showTableFooter"
      class="table-footer-wrap table-footer-sticky"
    >
      <table-footer
        :current-page="currentPage"
        :page-size="perPage"
        :total-count="filteredRecords.length"
        @page-change="onPageChange"
      />
    </div>

    <woot-modal :show.sync="showAddPopup" :on-close="hideAddPopup" :show-close-button="false" size="modal-medium">
      <add-label @close="hideAddPopup" />
    </woot-modal>

    <woot-modal :show.sync="showEditPopup" :on-close="hideEditPopup" :show-close-button="false" size="modal-medium">
      <edit-label
        :selected-response="selectedResponse"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      :show.sync="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('LABEL_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';

import AddLabel from './AddLabel';
import EditLabel from './EditLabel';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import alertMixin from 'shared/mixins/alertMixin';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';

export default {
  components: {
    AddLabel,
    EditLabel,
    TableFooter,
  },
  mixins: [alertMixin, uiSettingsMixin],
  data() {
    return {
      editLoading: {},
      deleteLoading: {},
      showAddPopup: false,
      showEditPopup: false,
      showDeleteConfirmationPopup: false,
      selectedResponse: {},
      activeTab: 'all', // default
      hasLoadedOnce: false,
      searchQuery: '',
      currentPage: 1,
      perPage: 20,
    };
  },
  computed: {
    ...mapGetters({
      records: 'labels/getLabels',
      uiFlags: 'labels/getUIFlags',
    }),
    tabFilteredRecords() {
      if (this.activeTab === 'all') {
        return this.records;
      }
      if (this.activeTab === 'automation') {
        return this.records.filter(
          label => label.automation || label.contact_automation
        );
      }
      if (this.activeTab === 'excluded') {
        return this.records.filter(label => label.exclude_from_default_views);
      }
      return this.records.filter(
        label =>
          !label.automation &&
          !label.contact_automation &&
          !label.exclude_from_default_views
      );
    },
    filteredRecords() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) {
        return this.tabFilteredRecords;
      }

      return this.tabFilteredRecords.filter(
        label =>
          (label.title && label.title.toLowerCase().includes(query)) ||
          (label.description &&
            label.description.toLowerCase().includes(query)) ||
          (label.color && label.color.toLowerCase().includes(query))
      );
    },
    paginatedRecords() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredRecords.slice(start, start + this.perPage);
    },
    emptyStateMessage() {
      if (this.searchQuery.trim()) {
        return this.$t('LABEL_MGMT.SEARCH_404');
      }
      return this.$t('LABEL_MGMT.LIST.404');
    },
    showTableFooter() {
      return (
        !this.uiFlags.isFetching &&
        this.hasLoadedOnce &&
        this.filteredRecords.length > 0
      );
    },
    // Delete Modal
    deleteConfirmText() {
      return this.$t('LABEL_MGMT.DELETE.CONFIRM.YES');
    },
    deleteRejectText() {
      return this.$t('LABEL_MGMT.DELETE.CONFIRM.NO');
    },
    deleteMessage() {
      return ` ${this.selectedResponse.title}?`;
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
  },
  created() {
    Promise.all([
      this.$store.dispatch('labels/get'),
      this.$store.dispatch('labels/getlabelsindex'),
    ]).finally(() => {
      this.hasLoadedOnce = true;
    });
  },
  methods: {
    onTabChange(tab) {
      this.activeTab = tab;
      this.currentPage = 1;
    },
    onSearchInput(event) {
      this.searchQuery = event.target.value;
    },
    applySearch() {
      this.currentPage = 1;
    },
    onPageChange(page) {
      this.currentPage = page;
    },
    descriptionTooltip(description) {
      if (!description || description.length <= 50) {
        return null;
      }
      return description;
    },
    adjustPageAfterDataChange() {
      const maxPage = Math.max(
        1,
        Math.ceil(this.filteredRecords.length / this.perPage)
      );
      if (this.currentPage > maxPage) {
        this.currentPage = maxPage;
      }
    },
    openAddPopup() {
      this.showAddPopup = true;
    },
    hideAddPopup() {
      this.showAddPopup = false;
    },

    async openEditPopup(response) {
      this.$set(this.editLoading, response.id, true);
      try {
        const responsedata = await this.$store.dispatch(
          'labels/getLabelsdata',
          response.id
        );
        this.selectedResponse = responsedata || response;
        this.showEditPopup = true;
      } finally {
        this.$set(this.editLoading, response.id, false);
      }
    },
    hideEditPopup() {
      this.showEditPopup = false;
    },

    openDeletePopup(response) {
      this.showDeleteConfirmationPopup = true;
      this.selectedResponse = response;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },

    confirmDeletion() {
      this.$set(this.deleteLoading, this.selectedResponse.id, true);
      this.closeDeletePopup();
      this.deleteLabel(this.selectedResponse.id);
    },
    // deleteLabel(id) {
    //   this.$store
    //     .dispatch('labels/delete', id)
    //     .then(() => {
    //       this.showAlert(this.$t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
    //     })
    //     .catch(() => {
    //       this.showAlert(this.$t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE'));
    //     })
    //     .finally(() => {
    //       this.loading[this.selectedResponse.id] = false;
    //     });
    // },

    async clearDeletedLabelFromSavedViews(deletedLabel) {
      const accountId = this.$route.params.accountId;
      if (!accountId || !deletedLabel?.title) return;

      const existingAccountPrefs =
        this.uiSettings?.user_preferences_by_account || {};
      const currentAccountPrefs = existingAccountPrefs[accountId] || {};
      const lastContactView = currentAccountPrefs.last_contact_view;
      const lastConversationView = currentAccountPrefs.last_conversation_view;
      const deletedTitle = String(deletedLabel.title);
      const updatedAccountPrefs = { ...currentAccountPrefs };

      if (
        lastContactView?.type === 'label' &&
        String(lastContactView.id) === deletedTitle
      ) {
        updatedAccountPrefs.last_contact_view = { type: 'all' };
      }

      if (
        lastConversationView?.type === 'label' &&
        String(lastConversationView.id) === deletedTitle
      ) {
        updatedAccountPrefs.last_conversation_view = { type: 'home' };
      }

      if (
        updatedAccountPrefs.last_contact_view !==
          currentAccountPrefs.last_contact_view ||
        updatedAccountPrefs.last_conversation_view !==
          currentAccountPrefs.last_conversation_view
      ) {
        await this.updateUISettings({
          user_preferences_by_account: {
            ...existingAccountPrefs,
            [accountId]: updatedAccountPrefs,
          },
        });
      }
    },
    redirectIfDeletedLabelIsOpen(deletedLabel) {
      const accountId = this.$route.params.accountId;
      if (!accountId || !deletedLabel?.title) return;

      const deletedTitle = String(deletedLabel.title);
      const { name, params } = this.$route;

      if (
        name === 'contacts_labels_dashboard' &&
        String(params.label) === deletedTitle
      ) {
        this.$router.push({
          name: 'contacts_dashboard',
          params: { accountId },
        });
        return;
      }

      if (
        (name === 'label_conversations' ||
          name === 'conversations_through_label') &&
        String(params.label) === deletedTitle
      ) {
        this.$router.push({
          name: 'home',
          params: { accountId },
        });
      }
    },
    deleteLabel(id) {
      const deletedLabel = this.records.find(label => label.id === id);

      this.$store
        .dispatch('labels/delete', id)
        .then(async () => {
          await this.clearDeletedLabelFromSavedViews(deletedLabel);
          this.redirectIfDeletedLabelIsOpen(deletedLabel);
          this.showAlert(this.$t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
        })
        .catch(error => {
          const apiMessage = error?.response?.data?.message;

          if (
            error?.response?.status === 422 &&
            apiMessage === 'Can not delete the priority label'
          ) {
            this.showAlert(apiMessage);
          } else {
            this.showAlert(this.$t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE'));
          }
        })
        .finally(() => {
          this.$set(this.deleteLoading, id, false);
          this.adjustPageAfterDataChange();
        });
    },

  },
};
</script>

<style scoped lang="scss">
@import 'dashboard/assets/scss/variables';

.labels-page {
  overflow: hidden;
}

.labels-page__body {
  min-height: 0;

  &--with-footer {
    padding-bottom: 50px;
  }
}

.labels-list-column {
  min-height: 0;
}

.labels-empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #64748b;

  @apply dark:text-slate-400;
}

.table-footer-wrap {
  flex-shrink: 0;
  min-width: 0;
}

.table-footer-sticky {
  position: fixed;
  right: 0;
  bottom: 0;
  width: calc(100vw - 3.5rem);
  z-index: 3;
  background: #f9f9f9;
  border-top: 1px solid #e5e7eb;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 -1px 3px rgba(0, 0, 0, 0.05);

  @apply dark:bg-slate-800 dark:border-slate-700;
}

.labels-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.label-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 320px;
  max-width: 100%;
  flex-shrink: 1;

  .search-icon-wrap {
    position: absolute;
    right: 12px;
    display: flex;
    align-items: center;
    height: 100%;
    cursor: pointer;
    z-index: 1;
    color: #94a3b8;

    @apply dark:text-slate-400;

    :deep(svg) {
      height: 18px;
      width: 18px;
    }
  }

  .label-search-input {
    width: 100%;
    height: 38px;
    padding: 0 40px 0 12px;
    font-size: 13px;
    border: 1px solid #ccc;
    border-radius: 6px;
    background: white;
    margin: 0;
    box-sizing: border-box;

    @apply dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200;

    &::placeholder {
      color: #9ca3af;
      @apply dark:text-slate-500;
    }

    &:focus {
      outline: none;
      border-color: #1f93ff;
      box-shadow: 0 0 0 1px #1f93ff;
    }
  }
}

.tabs-container {
  display: flex;
  gap: 12px;
}

.tab-button {
  min-width: auto;
  text-align: center;
  padding: 12px;
  border: none;
  cursor: pointer;
  background-color: transparent;
  color: #151718;
  transition: background-color 0.2s ease, color 0.2s ease;
  line-height: 1.2;
  margin-bottom: 8px;
  margin-top: 8px;
  font-weight: 600;

  @apply dark:text-slate-300;
}

.tab-button:hover {
  background-color: #f1f3f5;

  @apply dark:bg-slate-800 dark:text-slate-100;
}

.tab-button.active {
  background-color: transparent;
  color: #40518a;
  border-bottom: 3px solid #40518a;

  @apply dark:text-woot-400 dark:border-woot-400;

  &:hover {
    @apply dark:bg-transparent dark:text-woot-400;
  }
}

.label-color--container {
  @apply flex items-center;
}

.label-color--display {
  @apply rounded h-4 w-4 mr-1 rtl:mr-0 rtl:ml-1 border border-solid border-slate-50 dark:border-slate-700;
}

.automation-icon-wrap {
  @apply inline-flex items-center justify-center mr-1 rtl:mr-0 rtl:ml-1;
}

.automation-robot-icon {
  @apply w-4 h-4;
}
.labels-table {
  table-layout: fixed;
  width: 100%;
}

.label-title {
  width: 12rem;

  span {
    @apply w-full inline-block overflow-hidden whitespace-nowrap text-ellipsis;
  }
}

.label-description {
  width: 10rem;
  max-width: 10rem;

  .label-description-text {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    width: 100%;
    max-width: 10rem;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
}

.label-color-cell {
  @apply whitespace-nowrap pl-2 pr-8;
  width: 8.5rem;
}

.woot-table td.button-wrapper {
  @apply pl-2;
}

.woot-table td {
  vertical-align: top;
}
</style>
